/**
 * Drop-table presentation math, shared by the staff dashboard and the
 * storefront so both derive rarity and odds the same way.
 *
 * `packages/react/src/core/dropTable.ts` carries a byte-identical copy on
 * purpose: that package is published, so its `.d.ts` must not reference this
 * private one — the same constraint recorded in `packages/client/src/types.ts`.
 * `packages/react/src/core/dropTable.contract.test.ts` is the drift guard.
 * Thresholds are ported exactly from boxed-frontend; do not "tidy" them.
 */

/** 0 = common … 4 = top tier (5 reserved for future palettes/themes). */
export type Rarity = 0 | 1 | 2 | 3 | 4 | 5;

/** Provably-fair roll range; drop-table weights sum to exactly this. */
export const RESULT_RANGE = 1_000_000;

/**
 * Rarity 0-5 derived from the item-value / box-price ratio.
 *
 * Every degenerate ratio lands on 0: an unpriced draft (`boxPrice === 0`) gives
 * `Infinity` or `NaN`, so a whole table reads "Common" rather than throwing.
 * Callers previewing an unpriced box must say so in the UI — silently showing
 * an all-Common table is worse than showing nothing.
 */
export function getItemRarity(itemValue: number, boxPrice: number): Rarity {
  const rewardRatio: number = itemValue / boxPrice;
  if (rewardRatio === Number.POSITIVE_INFINITY) return 0;

  if (rewardRatio > 0.7 && rewardRatio < 1) return 1;
  if (rewardRatio >= 1 && rewardRatio < 1.5) return 2;
  if (rewardRatio >= 1.5 && rewardRatio < 6.5) return 3;
  if (rewardRatio >= 6.5) return 4;

  return 0;
}

/**
 * Weight → percent chance, rounded to 4 decimal places. Trailing zeros are
 * stripped (it returns a number): the dashboard's fixed-width `formatOdds`
 * exists precisely because this one is unsuitable for a monospace column.
 *
 * Deliberately a ratio of `RESULT_RANGE`, not of the table's own weight sum —
 * a half-edited table's chances add to less than 100%, which is the real
 * per-open probability such a table would produce.
 */
export function getItemChance(itemWeight: number): number {
  const chance = ((itemWeight || 0) / RESULT_RANGE) * 100;
  return Number(chance.toFixed(4));
}

/**
 * Best-first ordering, the way shoppers see a drop table. Returns a new array —
 * the caller's list is often React state.
 */
export function sortDropTableByValue<T extends { value: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => b.value - a.value);
}
